import { useState, useEffect } from 'react';
import { Plus, Loader2, AlertCircle, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SiteCard } from '../components/sites/SiteCard';
import { SiteForm } from '../components/sites/SiteForm';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../components/ui/toast';
import { useSites } from '../hooks/useSites';
import type { Site, CreateSiteDto, UpdateSiteDto } from '../types';

// Mock data for demo
const mockSites: Site[] = [
  {
    id: '1',
    user_id: 'user1',
    domain: 'example.com',
    name: 'My Main Website',
    api_key: 'cap_pk_live_abc123def456ghi789',
    secret_key: 'cap_sk_live_xyz987wvu654tsr321qpo098',
    is_active: true,
    allowed_origins: ['https://example.com'],
    verification_count: 5432,
    created_at: '2026-01-15T10:00:00Z',
    updated_at: '2026-02-20T15:30:00Z',
  },
  {
    id: '2',
    user_id: 'user1',
    domain: 'myshop.com',
    name: 'E-commerce Store',
    api_key: 'cap_pk_live_jkl456mno789pqr012',
    secret_key: 'cap_sk_live_stu345vwx678yza901bcd234',
    is_active: true,
    allowed_origins: ['https://myshop.com', 'https://www.myshop.com'],
    verification_count: 3210,
    created_at: '2026-02-01T08:00:00Z',
    updated_at: '2026-02-21T12:00:00Z',
  },
  {
    id: '3',
    user_id: 'user1',
    domain: 'blog.example.com',
    name: 'Blog',
    api_key: 'cap_pk_live_efg567hij890klm123',
    secret_key: 'cap_sk_live_nop456qrs789tuv012wxy345',
    is_active: false,
    allowed_origins: ['https://blog.example.com'],
    verification_count: 890,
    created_at: '2026-02-10T14:00:00Z',
    updated_at: '2026-02-18T09:00:00Z',
  },
];

export function SitesPage() {
  const navigate = useNavigate();
  const { sites: apiSites, loading, error, createSite, updateSite, deleteSite, regenerateKeys } = useSites();
  const { addToast } = useToast();

  const [sites, setSites] = useState<Site[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState<Site | null>(null);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [siteToRegenerate, setSiteToRegenerate] = useState<Site | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (apiSites.length > 0) {
      setSites(apiSites);
    } else if (!loading) {
      // Use mock data for demo
      setSites(mockSites);
    }
  }, [apiSites, loading]);

  const handleCreateOrUpdate = async (data: CreateSiteDto | UpdateSiteDto) => {
    setActionLoading(true);
    try {
      if (editingSite) {
        await updateSite(editingSite.id, data as UpdateSiteDto);
        addToast({ title: 'Site updated', type: 'success' });
      } else {
        await createSite(data as CreateSiteDto);
        addToast({ title: 'Site created', type: 'success' });
      }
      setFormOpen(false);
      setEditingSite(null);
    } catch (err) {
      addToast({
        title: 'Error',
        description: (err as Error).message,
        type: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!siteToDelete) return;
    setActionLoading(true);
    try {
      await deleteSite(siteToDelete.id);
      setSites((prev) => prev.filter((s) => s.id !== siteToDelete.id));
      addToast({ title: 'Site deleted', type: 'success' });
      setDeleteDialogOpen(false);
      setSiteToDelete(null);
    } catch (err) {
      addToast({
        title: 'Delete failed',
        description: (err as Error).message,
        type: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateKeys = async () => {
    if (!siteToRegenerate) return;
    setActionLoading(true);
    try {
      const updated = await regenerateKeys(siteToRegenerate.id);
      setSites((prev) => prev.map((s) => (s.id === siteToRegenerate.id ? updated : s)));
      addToast({ title: 'Keys regenerated', type: 'success' });
      setRegenerateDialogOpen(false);
      setSiteToRegenerate(null);
    } catch (err) {
      addToast({
        title: 'Error',
        description: (err as Error).message,
        type: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const openEdit = (site: Site) => {
    setEditingSite(site);
    setFormOpen(true);
  };

  const openDelete = (site: Site) => {
    setSiteToDelete(site);
    setDeleteDialogOpen(true);
  };

  const openRegenerate = (site: Site) => {
    setSiteToRegenerate(site);
    setRegenerateDialogOpen(true);
  };

  const viewStats = (site: Site) => {
    navigate(`/analytics?site=${site.id}`);
  };

  if (loading && sites.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Sites</h1>
          <p className="text-muted-foreground">
            Manage your websites and API keys
          </p>
        </div>
        <Button onClick={() => { setEditingSite(null); setFormOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />
          Add Site
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 text-sm text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
          <AlertCircle className="h-4 w-4" />
          Using demo data. Connect to API for real data.
        </div>
      )}

      {/* Sites grid */}
      {sites.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {sites.map((site) => (
            <SiteCard
              key={site.id}
              site={site}
              onEdit={openEdit}
              onDelete={openDelete}
              onRegenerateKeys={openRegenerate}
              onViewStats={viewStats}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed rounded-lg">
          <Globe className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">No sites yet</h3>
          <p className="text-muted-foreground mb-4">Add your first site to get started</p>
          <Button onClick={() => setFormOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Site
          </Button>
        </div>
      )}

      {/* Create/Edit form */}
      <SiteForm
        open={formOpen}
        onOpenChange={setFormOpen}
        site={editingSite}
        onSubmit={handleCreateOrUpdate}
        loading={actionLoading}
      />

      {/* Delete confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Site?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{siteToDelete?.name}</strong>?
              This action cannot be undone. All API keys will be deactivated.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate keys confirmation */}
      <Dialog open={regenerateDialogOpen} onOpenChange={setRegenerateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate Keys?</DialogTitle>
            <DialogDescription>
              Are you sure you want to regenerate API keys for <strong>{siteToRegenerate?.name}</strong>?
              Old keys will stop working immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegenerateDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleRegenerateKeys} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Regenerate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
